import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faImages } from '@fortawesome/pro-light-svg-icons/faImages';
import { isSameMediaWidgetSource, MediaWidgetSource } from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../../../common/types';
import { LoadingView } from '../../../../../common/views/LoadingView';
import { ErrorView } from '../../../../../common/views/ErrorView/ErrorView';
import { SMLEmptyState } from '../../../../../music24/components/SMLEmptyState/SMLEmptyState';
import { pickerMessages } from '../../../../../music24/promote/composer/picker/pickerMessages';
import { PickerTile } from '../../../../../music24/promote/composer/picker/PickerTile';
import { mediaBlockMessages } from '../mediaBlockMessages';
import { MediaBlockPick } from './mediaBlockPick';
import { MediaBlockTile } from './mediaBlockTiles';
import { MediaBlockPicker } from './useMediaBlockPicker';
/* eslint-disable css-modules/no-unused-class -- the picker stylesheet of the Composer; the tiles use their slice of it */
import pickerStyles from '../../../../../music24/promote/composer/picker/ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaPickerTilesProps {
  picker: MediaBlockPicker;
  selected: MediaWidgetSource | null;
  onPick: (pick: MediaBlockPick) => void;
  onOpenStudio: () => void;
}

interface TileListProps {
  tiles: MediaBlockTile[];
  selected: MediaWidgetSource | null;
  onSelect: (key: string) => void;
}

const TileList: React.FC<TileListProps> = ({ tiles, selected, onSelect }) => {
  const intl = useIntl();
  return (
    <ul className={pickerStyles.tileList}>
      {tiles.map(({ key, pick, revision }) => (
        <PickerTile
          key={key}
          tileKey={key}
          name={pick.label}
          facts={pick.facts}
          kind={pick.kind}
          pictureUrl={pick.pictureUrl}
          badge={revision === undefined ? undefined : intl.formatMessage(pickerMessages.revision, { revision })}
          isSelected={selected !== null && isSameMediaWidgetSource(selected, pick.source)}
          onSelect={onSelect}
          onAttach={onSelect}
        />
      ))}
    </ul>
  );
};

/**
 * The cover and the newest ready files of the picked song as Composer picker tiles, or of every
 * song under a song heading ("All songs"), or the loading, error and empty states (design a2, a6).
 */
const MediaPickerTilesComponent: React.FC<MediaPickerTilesProps> = ({ picker, selected, onPick, onOpenStudio }) => {
  const intl = useIntl();
  const { songTiles, tileCount, isAllSongs, filter, isLoading, error, refetch } = picker;
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const tilesByKey = useMemo(
    () => new Map(songTiles.flatMap((song) => song.tiles).map((tile) => [tile.key, tile] as const)),
    [songTiles]
  );
  const pickKey = useCallback(
    (key: string) => {
      const found = tilesByKey.get(key);
      if (found !== undefined) {
        onPick(found.pick);
      }
    },
    [onPick, tilesByKey]
  );

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (tileCount === 0) {
    return (
      <div data-testid="media-picker-empty">
        <SMLEmptyState
          icon={faImages}
          title={intl.formatMessage(isAllSongs ? pickerMessages.noSongTitle : pickerMessages.emptyTitle)}
          description={intl.formatMessage(isAllSongs ? mediaBlockMessages.allSongsEmpty : mediaBlockMessages.emptyBody)}
          action={{ label: intl.formatMessage(pickerMessages.openStudio), onClick: onOpenStudio }}
        />
        <p className={pickerStyles.quietNote}>{intl.formatMessage(mediaBlockMessages.emptyNote)}</p>
      </div>
    );
  }
  if (songTiles.length === 0) {
    return (
      <p className={pickerStyles.quietNote}>
        {intl.formatMessage(filter === 'image' ? pickerMessages.noImages : pickerMessages.noVideos)}
      </p>
    );
  }
  if (!isAllSongs) {
    return (
      <div data-testid="media-picker-files">
        <TileList tiles={songTiles[0]?.tiles ?? []} selected={selected} onSelect={pickKey} />
      </div>
    );
  }

  return (
    <div className={styles.songGroups} data-testid="media-picker-files">
      {songTiles.map((song) => (
        <section
          key={song.catalogItemUuid}
          className={styles.songGroup}
          data-testid={`media-picker-song-${song.catalogItemUuid}`}
        >
          <h6 className={`${pickerStyles.sectionName} ${styles.songName}`}>{song.title}</h6>
          <TileList tiles={song.tiles} selected={selected} onSelect={pickKey} />
        </section>
      ))}
    </div>
  );
};

export const MediaPickerTiles = TypedMemo(MediaPickerTilesComponent);
